import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { usePendingDashboardActions } from '../../hooks/usePendingDashboardActions';
import userIcon from '../../Assets/images/user2.svg';
import editIcon from '../../Assets/images/edit2.svg';
import cardIcon from '../../Assets/images/card.svg';
import './pending-actions.scss';
import './pending-actions-flow.scss';
import { LanguageConstants } from '../../constants/LanguageConstants';

const PENDING_ACTIONS_HOST_ID = 'dashboard-pending-actions-host';

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`pending-actions__chevron${open ? ' pending-actions__chevron--open' : ''}`}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function PendingActionsBar() {
  const { items, loading } = usePendingDashboardActions();
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeId, setActiveId] = useState('');
  const [barHeight, setBarHeight] = useState(0);
  const [hostEl, setHostEl] = useState<HTMLElement | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    setHostEl(document.getElementById(PENDING_ACTIONS_HOST_ID));
  }, []);

  useEffect(() => {
    if (!items.length) {
      setActiveId('');
      return;
    }
    setActiveId((prev) => {
      if (items.length === 1) return items[0].id;
      if (prev && items.some((i) => i.id === prev)) return prev;
      return items[0].id;
    });
  }, [items]);

  useLayoutEffect(() => {
    if (loading || !items.length) {
      setBarHeight(0);
      return;
    }
    const el = rootRef.current;
    if (!el) return;

    const syncHeight = () => setBarHeight(Math.round(el.getBoundingClientRect().height));
    syncHeight();

    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncHeight) : null;
    ro?.observe(el);

    return () => ro?.disconnect();
  }, [loading, items.length, panelOpen, activeId, hostEl]);

  if (loading || !items.length) {
    return null;
  }

  // Wait for the fixed header host so we never paint the bar in page flow first.
  if (!hostEl) {
    return (
      <div
        className="pending-actions-spacer"
        style={{ height: barHeight || undefined }}
        aria-hidden
      />
    );
  }

  const count = items.length;

  const iconFor = (variant: (typeof items)[0]['variant'], context: 'strip' | 'cta') => {
    const src = variant === 'profile' ? userIcon : variant === 'draft' ? editIcon : cardIcon;
    return (
      <img
        src={src}
        alt=""
        aria-hidden
        className={`pending-actions__icon pending-actions__icon--${variant} pending-actions__icon--${context}`}
      />
    );
  };

  /** Accordion: exactly one card shows details; single item always stays expanded. */
  const toggleCard = (id: string) => {
    if (items.length === 1) {
      setActiveId(items[0].id);
      return;
    }
    setActiveId(id);
  };

  const bar = (
    <div
      ref={rootRef}
      className={`pending-actions${panelOpen ? ' pending-actions--open' : ''}`}
      id="dashboard-pending-actions"
    >
      <div className="container">
        <div className="pending-actions__bar cursor-pointer" onClick={() => setPanelOpen((v) => !v)}>
          <div className="pending-actions__left">
            <p className="pending-actions__title">
              {(LanguageConstants as any)?.PENDING_ACTIONS_TITLE ?? 'Pending Actions'}
            </p>
            <span className="pending-actions__badge" aria-label={`${count} pending`}>
              {count}
            </span>
          </div>
          <button
            type="button"
            className="pending-actions__toggle"
            aria-expanded={panelOpen}
            aria-controls="pending-actions-panel"
          >
            <ChevronIcon open={panelOpen} />
          </button>
        </div>
      </div>

      <div
        className="pending-actions__panel"
        id="pending-actions-panel"
        role="region"
        aria-hidden={!panelOpen}
        aria-label={(LanguageConstants as any)?.PENDING_ACTIONS_DETAIL_ARIA ?? 'Pending actions detail'}
      >
        <div className="container">
          <div className="pending-actions__panel-inner">
            {items.map((item) => {
              const expanded = activeId === item.id;
              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={panelOpen ? 0 : -1}
                  className={`pending-actions__card pending-actions__card--${item.variant} ${
                    expanded ? 'pending-actions__card--expanded' : 'pending-actions__card--collapsed'
                  }`}
                  aria-expanded={expanded}
                  aria-controls={`pending-action-body-${item.id}`}
                  onClick={() => toggleCard(item.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleCard(item.id);
                    }
                  }}
                >
                  <div className="pending-actions__card-strip">
                    <span className="pending-actions__card-icon-wrap">{iconFor(item.variant, 'strip')}</span>
                    <p className="pending-actions__card-strip-text">{item.stripLabel}</p>
                  </div>
                  {expanded ? (
                    <div className="pending-actions__card-body" id={`pending-action-body-${item.id}`}>
                      <p className="pending-actions__card-desc">{item.description}</p>
                      <Link
                        className="pending-actions__card-cta"
                        to={item.to}
                        onClick={(e) => e.stopPropagation()}
                        tabIndex={panelOpen ? 0 : -1}
                      >
                        {iconFor(item.variant, 'cta')}
                        {item.ctaLabel}
                      </Link>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Reserves layout space under the fixed chrome so content is not covered. */}
      <div
        className="pending-actions-spacer"
        style={{ height: barHeight || undefined }}
        aria-hidden
      />
      {createPortal(bar, hostEl)}
    </>
  );
}
